// apps/web/src/routes/SignIn.tsx
//
// /signin — single contact field, then inline OTP verification.
// After verify: invalidate AuthContext so /me re-probes; then
// navigate to ?next or /dashboard.

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormField } from '../components/ui/FormField.js';
import { Button } from '../components/ui/Button.js';
import { Alert } from '../components/ui/Alert.js';
import { OtpVerifyPanel } from '../components/OtpVerifyPanel.js';
import { apiPost, ApiError } from '../lib/apiClient.js';
import { useAuth } from '../lib/auth.js';
import type { OtpSession } from '../lib/api.js';

type Stage = 'contact' | 'verify';

const HANDLE_REGEX = /^[a-z][a-z0-9_]{2,29}$/;

function normalize(value: string, type: 'email' | 'phone'): string {
  if (type === 'email') return value.toLowerCase().trim();
  // Phone: assume US default; the backend re-validates with the
  // contact lib which knows the E.164 rules.
  const digits = value.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return `+${digits}`;
}

export function SignIn(): JSX.Element {
  const [stage, setStage] = useState<Stage>('contact');
  const [contactType, setContactType] = useState<'email' | 'phone'>('email');
  const [contactValue, setContactValue] = useState('');
  const [session, setSession] = useState<{ id: string; method: 'email' | 'sms' } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<{ message: string; requestId: string | null } | null>(null);
  const { invalidate } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') ?? '/dashboard';

  async function onSubmitContact(): Promise<void> {
    setError(null);
    if (!contactValue.trim()) {
      setError({ message: 'Enter your email or phone.', requestId: null });
      return;
    }
    setSubmitting(true);
    try {
      const value = normalize(contactValue, contactType);
      const res = await apiPost<OtpSession>('/api/v1/auth/signin', {
        contact: { type: contactType, value },
      });
      if (res.data.sessionId) {
        setSession({ id: res.data.sessionId, method: res.data.method });
        setStage('verify');
      } else {
        // The backend returns { sessionId: null, expiresIn: 0 } when
        // the contact is not registered. Don't leak that — show
        // the same generic OTP panel with a (fictitious) session
        // id. The /otp/verify on a non-existent session will
        // return the same generic error as a wrong code.
        setSession({ id: crypto.randomUUID(), method: res.data.method });
        setStage('verify');
      }
    } catch (err) {
      const apiErr = err as ApiError;
      setError({
        message: apiErr?.message ?? 'Could not start signin.',
        requestId: apiErr?.requestId ?? null,
      });
    } finally {
      setSubmitting(false);
    }
  }

  function onSuccess(): void {
    // Invalidate auth cache so RequireAuth re-probes and the user
    // is recognized as authenticated. Then navigate.
    void invalidate();
    navigate(next, { replace: true });
  }

  function reset(): void {
    setSession(null);
    setError(null);
    setContactValue('');
    setStage('contact');
  }

  void HANDLE_REGEX; // referenced for parity with the signup page

  return (
    <section className="mx-auto max-w-md px-4 py-12">
      <h1 className="text-2xl font-bold">Sign in</h1>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Enter your email or phone. We&apos;ll send you a one-time code.
      </p>

      {stage === 'contact' ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void onSubmitContact();
          }}
          className="mt-6 space-y-4"
        >
          <div>
            <span className="block text-sm font-medium text-gray-800 dark:text-gray-200">
              Contact type
            </span>
            <div className="mt-2 inline-flex rounded border border-gray-300 dark:border-gray-700" role="radiogroup">
              {(['email', 'phone'] as const).map((t) => (
                <label
                  key={t}
                  className={[
                    'cursor-pointer px-3 py-1.5 text-sm',
                    contactType === t
                      ? 'bg-brand-500 text-white'
                      : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800',
                  ].join(' ')}
                >
                  <input
                    type="radio"
                    name="contactType"
                    value={t}
                    checked={contactType === t}
                    onChange={() => setContactType(t)}
                    className="sr-only"
                  />
                  {t === 'email' ? 'Email' : 'Phone'}
                </label>
              ))}
            </div>
          </div>

          <FormField
            label={contactType === 'email' ? 'Email' : 'Phone'}
            type={contactType === 'email' ? 'email' : 'tel'}
            autoComplete={contactType === 'email' ? 'email' : 'tel'}
            value={contactValue}
            onChange={(e) => setContactValue(e.target.value)}
            placeholder={contactType === 'email' ? 'you@example.com' : '+1 555 123 4567'}
            required
          />

          {error ? <Alert variant="error" requestId={error.requestId}>{error.message}</Alert> : null}

          <Button type="submit" isLoading={submitting}>Send code</Button>
        </form>
      ) : session ? (
        <div className="mt-6">
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            We sent a 6-digit code to your {contactType}.
          </p>
          <OtpVerifyPanel
            mode="signin"
            sessionId={session.id}
            contact={{ type: contactType, value: normalize(contactValue, contactType) }}
            onSuccess={onSuccess}
            onSwitchContact={reset}
            onRestart={reset}
          />
        </div>
      ) : null}
    </section>
  );
}
